import { Image } from 'react-native';
import { AtsBoardIcon, BaytIcon, GoogleJobsIcon, LinkedinIcon } from '../components/common/BrandIcons';

const TABBIO_LOGO = require('../../assets/branding/logo-compact.png');

type MatchTone = 'strong' | 'medium' | 'low';
type CatalogSourceKey =
  | 'tabbio_native'
  | 'google_jobs_dataforseo'
  | 'ats_workable'
  | 'partner_ats';

export function hasCatalogMatchScore(score: number | null | undefined): score is number {
  return typeof score === 'number' && score > 0;
}

export function getMatchTone(score: number | null): MatchTone {
  if (score == null) return 'low';
  if (score >= 0.28) return 'strong';
  if (score >= 0.1) return 'medium';
  return 'low';
}

export function getDiscoveryAlignmentKey(score: number | null) {
  return `jobs.alignment.${getMatchTone(score)}` as const;
}

export function getNativeApplicationMethod(applyMode: string): 'tabbio' | 'external' {
  return applyMode === 'tabbio_native' ? 'tabbio' : 'external';
}

export function getCatalogSourceLabelKey(source: string) {
  switch (source as CatalogSourceKey) {
    case 'tabbio_native':
      return 'jobs.catalog.source.tabbioNative';
    case 'google_jobs_dataforseo':
      return 'jobs.catalog.source.googleJobs';
    case 'ats_workable':
      return 'jobs.catalog.source.workable';
    case 'partner_ats':
      return 'jobs.catalog.source.partnerAts';
    default:
      return 'jobs.catalog.source.external';
  }
}

export function getCatalogApplyModeLabelKey(applyMode: string, easyApply = true) {
  if (applyMode === 'tabbio_native' && !easyApply) {
    return 'jobs.catalog.applyMode.externalRedirect';
  }

  switch (applyMode) {
    case 'tabbio_native':
      return 'jobs.catalog.applyMode.tabbioNative';
    case 'external_redirect':
      return 'jobs.catalog.applyMode.externalRedirect';
    case 'partner_ats_managed':
      return 'jobs.catalog.applyMode.partnerAtsManaged';
    default:
      return 'jobs.catalog.applyMode.externalRedirect';
  }
}

export function getCatalogPrimaryLabelKey(applyMode: string, applied: boolean, easyApply = true) {
  if (applied) {
    return 'jobs.applied';
  }

  if (applyMode === 'tabbio_native' && !easyApply) {
    return 'jobs.applyOnWebsite';
  }

  switch (applyMode) {
    case 'tabbio_native':
      return 'jobs.catalog.cta.oneTap';
    case 'partner_ats_managed':
      return 'jobs.catalog.cta.partnerAts';
    default:
      return 'jobs.applyOnWebsite';
  }
}

export function getCatalogActionLabelKey(applyMode: string, applied: boolean, easyApply = true) {
  return getCatalogPrimaryLabelKey(applyMode, applied, easyApply);
}

export function getCatalogKindLabelKey(kind: string) {
  return kind === 'native'
    ? 'jobs.catalog.kind.native'
    : 'jobs.catalog.kind.external';
}

export function CatalogSourceIcon({
  source,
  sourceLabel = '',
  size = 14,
}: {
  source: string;
  sourceLabel?: string;
  size?: number;
}) {
  const label = sourceLabel.toLowerCase();
  switch (source as CatalogSourceKey) {
    case 'tabbio_native':
      return <Image source={TABBIO_LOGO} style={{ width: size, height: size }} resizeMode="contain" />;
    case 'google_jobs_dataforseo':
      if (label.includes('linkedin')) return <LinkedinIcon size={size} />;
      if (label.includes('bayt')) return <BaytIcon size={size} />;
      return <GoogleJobsIcon size={size} />;
    case 'ats_workable':
    case 'partner_ats':
      return <AtsBoardIcon size={size} />;
    default:
      return <GoogleJobsIcon size={size} />;
  }
}
